/**
 * AWARE Health — Language Selector Component
 * Compact dropdown for switching UI language.
 *
 * Shows a globe + native short label instead of only a flag, because
 * Windows browsers render flag emoji as plain letters ("GB", "VN").
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'

const LANGUAGES = [
  { code: 'en',    short: 'EN',     label: 'English' },
  { code: 'vi',    short: 'VI',     label: 'Tiếng Việt' },
  { code: 'zh-TW', short: '中文',   label: '繁體中文' },
  { code: 'ne',    short: 'नेपाली', label: 'नेपाली' },
]

export default function LanguageSelector() {
  const { t, i18n } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const activeCode = i18n.resolvedLanguage ?? i18n.language
  const current = LANGUAGES.find((l) => l.code === activeCode) ?? LANGUAGES[0]

  // Close on outside click or Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        title={t('language.select')}
        aria-label={t('language.select')}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          color: isDark ? '#cbd5e1' : '#334155',
          fontFamily: 'Inter, sans-serif',
          transition: 'all 0.2s',
        }}
      >
        <span style={{ fontSize: 14 }} aria-hidden>🌐</span>
        <span>{current.short}</span>
        <span
          aria-hidden
          style={{
            fontSize: 8,
            opacity: 0.6,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className="animate-fade-in-up"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            minWidth: 170,
            borderRadius: 10,
            background: isDark ? '#0f1a2e' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: isDark
              ? '0 12px 40px rgba(0,0,0,0.6)'
              : '0 12px 40px rgba(0,0,0,0.12)',
            zIndex: 9999,
            padding: 4,
            overflow: 'hidden',
          }}
        >
          {LANGUAGES.map((lang) => {
            const active = activeCode === lang.code
            return (
              <button
                key={lang.code}
                role="option"
                aria-selected={active}
                onClick={() => {
                  i18n.changeLanguage(lang.code)
                  setOpen(false)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: active
                    ? (isDark ? 'rgba(20,184,166,0.15)' : 'rgba(13,148,136,0.1)')
                    : 'transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontFamily: 'Inter, sans-serif',
                  textAlign: 'left',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent'
                }}
              >
                <span>{lang.label}</span>
                {active && <span style={{ color: isDark ? '#2dd4bf' : '#0d9488', fontWeight: 700 }}>✓</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
